import { Link } from "@tanstack/react-router";
import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { Pill, TableSkeleton } from "../components/Pill";
import {
  PAYMENT_ATTACHMENT_CLASSIFICATION_UI,
  PAYMENT_LINK_STATUS_UI,
  PAYMENT_VALIDATION_STATUS_UI,
} from "../lib/enumUI";
import {
  paymentOrderDetailQueryOptions,
  requestPaymentOrderValidation,
} from "./queries";
import type {
  PaymentOrderAttachment,
  PaymentOrderValidationRun,
} from "./types";

export function PaymentOrderDetail({ emailId }: { emailId: string }) {
  const queryClient = useQueryClient();
  const detailQuery = useQuery(paymentOrderDetailQueryOptions(emailId));

  const validateMutation = useMutation({
    mutationFn: () => requestPaymentOrderValidation(emailId),
    onSuccess: () => {
      // The 202 only returns a run id; re-fetch the detail so the new
      // run appears in the validation history panel.
      queryClient.invalidateQueries({
        queryKey: ["payment-orders", emailId, "detail"],
      });
      queryClient.invalidateQueries({ queryKey: ["payment-orders"] });
    },
  });

  if (detailQuery.isLoading) {
    return (
      <div className="page">
        <TableSkeleton />
      </div>
    );
  }
  if (detailQuery.error) {
    const message =
      detailQuery.error instanceof Error
        ? detailQuery.error.message
        : String(detailQuery.error);
    return (
      <div className="page">
        <p className="alert" role="alert">
          Failed to load payment order: {message}
        </p>
      </div>
    );
  }
  const data = detailQuery.data;
  if (!data) return null;

  return (
    <div className="page">
      <p className="page-breadcrumb">
        <Link to="/payment-orders">← All payment orders</Link>
      </p>
      <h1>{data.subject ?? <em>(no subject)</em>}</h1>
      <dl className="meta-dl">
        <div>
          <dt>Message id</dt>
          <dd>
            <code>{data.message_id}</code>
          </dd>
        </div>
        <div>
          <dt>Sender</dt>
          <dd>{data.sender}</dd>
        </div>
        <div>
          <dt>Received</dt>
          <dd>{new Date(data.received_at).toLocaleString()}</dd>
        </div>
        <div>
          <dt>Project link</dt>
          <dd>
            <Pill tone={PAYMENT_LINK_STATUS_UI[data.project_link_status].tone}>
              {PAYMENT_LINK_STATUS_UI[data.project_link_status].label}
            </Pill>
            {data.linked_project_id ? (
              <>
                {" "}
                <Link
                  to="/projects/$projectId"
                  params={{ projectId: data.linked_project_id }}
                >
                  view project →
                </Link>
              </>
            ) : null}
          </dd>
        </div>
        <div>
          <dt>Latest validation</dt>
          <dd>
            {data.latest_validation_status ? (
              <Pill
                tone={
                  PAYMENT_VALIDATION_STATUS_UI[data.latest_validation_status]
                    .tone
                }
              >
                {
                  PAYMENT_VALIDATION_STATUS_UI[data.latest_validation_status]
                    .label
                }
              </Pill>
            ) : (
              "—"
            )}
          </dd>
        </div>
      </dl>

      <ValidateAction
        onValidate={() => validateMutation.mutate()}
        isPending={validateMutation.isPending}
        error={validateMutation.error}
        lastRunId={validateMutation.data?.validation_run_id}
      />

      <AttachmentsTable attachments={data.attachments} />
      <ValidationHistory history={data.validation_history} />
    </div>
  );
}

function ValidateAction({
  onValidate,
  isPending,
  error,
  lastRunId,
}: {
  onValidate: () => void;
  isPending: boolean;
  error: unknown;
  lastRunId: string | undefined;
}) {
  return (
    <p>
      <button
        type="button"
        className="btn"
        onClick={onValidate}
        disabled={isPending}
      >
        {isPending ? "Queueing validation…" : "Re-run validation"}
      </button>
      {error ? (
        <span className="inline-status inline-status--error" role="alert">
          failed: {error instanceof Error ? error.message : String(error)}
        </span>
      ) : null}
      {lastRunId && !isPending && !error ? (
        <span className="inline-status">
          queued run <code>{lastRunId}</code>
        </span>
      ) : null}
    </p>
  );
}

function AttachmentsTable({
  attachments,
}: {
  attachments: PaymentOrderAttachment[];
}) {
  if (attachments.length === 0) {
    return (
      <section>
        <h2>Attachments</h2>
        <p className="empty-state">No attachments.</p>
      </section>
    );
  }
  return (
    <section>
      <h2>Attachments</h2>
      <table className="data-table">
        <thead>
          <tr>
            <th>File</th>
            <th>Classification</th>
            <th>Content hash</th>
            <th>Recorded</th>
          </tr>
        </thead>
        <tbody>
          {attachments.map((a) => (
            <tr key={a.id}>
              <td>{a.file_name}</td>
              <td>
                <Pill
                  tone={
                    PAYMENT_ATTACHMENT_CLASSIFICATION_UI[a.classification_status]
                      .tone
                  }
                >
                  {
                    PAYMENT_ATTACHMENT_CLASSIFICATION_UI[a.classification_status]
                      .label
                  }
                </Pill>
              </td>
              <td>
                {a.content_hash ? <code>{a.content_hash}</code> : "—"}
              </td>
              <td>{new Date(a.created_at).toLocaleString()}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

function ValidationHistory({
  history,
}: {
  history: PaymentOrderValidationRun[];
}) {
  if (history.length === 0) {
    return (
      <section>
        <h2>Validation history</h2>
        <p className="empty-state">No validation runs recorded yet.</p>
      </section>
    );
  }
  return (
    <section>
      <h2>Validation history</h2>
      <table className="data-table">
        <thead>
          <tr>
            <th>Executed</th>
            <th>Status</th>
            <th>Policy snapshot</th>
          </tr>
        </thead>
        <tbody>
          {history.map((run) => (
            <tr key={run.id}>
              <td>{new Date(run.executed_at).toLocaleString()}</td>
              <td>
                <Pill
                  tone={PAYMENT_VALIDATION_STATUS_UI[run.validation_status].tone}
                >
                  {PAYMENT_VALIDATION_STATUS_UI[run.validation_status].label}
                </Pill>
              </td>
              <td>
                <PolicySnapshot snapshot={run.policy_snapshot} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

function PolicySnapshot({
  snapshot,
}: {
  snapshot: Record<string, unknown>;
}) {
  const entries = Object.entries(snapshot);
  if (entries.length === 0) return <span>—</span>;
  return (
    <details>
      <summary>view</summary>
      <pre>{JSON.stringify(snapshot, null, 2)}</pre>
    </details>
  );
}
